import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { UserAvatar } from '@/components/UserAvatar';
import { api } from '@/lib/api';
import { TRIP_SETTINGS } from '@/constants/testIds';
import { Crown, Check, Clock, X, Copy, Link2 } from 'lucide-react';
import { toast } from 'sonner';

export default function TripSettingsDialog({ open, onOpenChange, trip, isOwner, currentUserId, onUpdated }) {
    const [removingId, setRemovingId] = useState(null);
    const inviteUrl = trip ? `${window.location.origin}/join/${trip.invite_code}` : '';

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(inviteUrl);
            toast.success('Invite link copied');
        } catch { toast.error('Copy failed'); }
    };

    const remove = async (userId) => {
        setRemovingId(userId);
        try {
            const { data } = await api.delete(`/trips/${trip.trip_id}/participants/${userId}`);
            toast.success('Participant removed');
            onUpdated?.(data.trip);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not remove');
        } finally {
            setRemovingId(null);
        }
    };

    if (!trip) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid={TRIP_SETTINGS.dialog}
                className="rounded-3xl max-w-lg border-[var(--ns-border)] max-h-[90vh] overflow-y-auto"
            >
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl font-semibold tracking-tight">Trip members</DialogTitle>
                    <DialogDescription className="text-[var(--ns-text2)]">
                        {trip.participants.length} {trip.participants.length === 1 ? 'traveler' : 'travelers'} on this trip.
                    </DialogDescription>
                </DialogHeader>

                <div className="mt-2 rounded-2xl border border-[var(--ns-border)] bg-[var(--ns-soft)] p-4">
                    <div className="text-xs uppercase tracking-widest text-[var(--ns-text3)] mb-2 flex items-center gap-1.5">
                        <Link2 size={12} /> Invite link
                    </div>
                    <div className="flex items-center gap-2">
                        <code className="flex-1 truncate text-sm bg-white border border-[var(--ns-border)] rounded-xl px-3 py-2 font-mono text-[var(--ns-text2)]">
                            {inviteUrl}
                        </code>
                        <Button
                            onClick={copy}
                            data-testid={TRIP_SETTINGS.shareButton}
                            className="rounded-xl h-10 px-3 text-white"
                            style={{ background: 'var(--ns-navy)' }}
                        >
                            <Copy size={16} />
                        </Button>
                    </div>
                </div>

                <div className="mt-4 divide-y divide-[var(--ns-border)]">
                    {trip.participants.map((p) => {
                        const owner = p.user_id === trip.owner_id;
                        const isSelf = p.user_id === currentUserId;
                        const submitted = !!p.preferences_submitted;
                        return (
                            <div
                                key={p.user_id}
                                data-testid={TRIP_SETTINGS.participantRow}
                                className="flex items-center justify-between py-3"
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <UserAvatar name={p.name} picture={p.picture} size={36} />
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium text-[var(--ns-text)] flex items-center gap-2">
                                            <span className="truncate">{p.name || 'Traveler'}{isSelf && <span className="text-[var(--ns-text3)] font-normal"> (you)</span>}</span>
                                            {owner && (
                                                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full text-white" style={{ background: 'var(--ns-clay)' }}>
                                                    <Crown size={10} /> Owner
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-xs text-[var(--ns-text3)] mt-0.5 inline-flex items-center gap-1">
                                            {submitted ? <><Check size={11} className="text-[var(--ns-success)]" /> Preferences submitted</> : <><Clock size={11} /> Waiting for preferences</>}
                                        </div>
                                    </div>
                                </div>
                                {isOwner && !owner && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => remove(p.user_id)}
                                        disabled={removingId === p.user_id}
                                        data-testid={TRIP_SETTINGS.removeParticipantButton}
                                        className="rounded-full h-9 px-3 text-[var(--ns-text2)] hover:text-[var(--ns-clayHover)] hover:bg-[var(--ns-clay)]/10"
                                    >
                                        <X size={14} className="mr-1" /> Remove
                                    </Button>
                                )}
                            </div>
                        );
                    })}
                </div>

                {!isOwner && (
                    <p className="text-xs text-[var(--ns-text3)] mt-4">Only the trip owner can remove participants.</p>
                )}
            </DialogContent>
        </Dialog>
    );
}
